import { Component, OnInit, Output, EventEmitter, Input, ViewChild, OnDestroy } from '@angular/core';
import { FormGroup, FormControl } from '@angular/forms';
import { MatPaginator } from '@angular/material/paginator';

import { Store } from '@ngrx/store';
import { take, map } from 'rxjs/operators';

import { AppState, getMyAuth, getMyGlossari } from 'src/app/store/app.state';
import * as glossariActions from '../../../glossari/store/glossari.actions';
import { GlossaryType } from '../../models/glossary-type.model';
import { cercaGlossariParent, GlossariService } from 'src/app/glossari/glossari.service';
import { Glossary } from '../../models/glossary.model';
import { UtilityService } from '../../services/utility.service';
import { SubSink } from 'subsink';
import { SingleFilter } from '../../models/filter-data/single-filter.model';
import { operatorValue } from '../../models/filter-data/operator-filter.model';

/**Componente per il modale di ricerca glossario */
@Component({
  selector: 'app-modale-cerca-glossari',
  templateUrl: './modale-cerca-glossari.component.html',
  styleUrls: ['./modale-cerca-glossari.component.css', '../../../shared/mediaquery.css']
})
export class ModaleCercaGlossariComponent implements OnInit, OnDestroy {
  private subs = new SubSink();
  /**Emettitore per l'evento di chiusura del modale */
  @Output() close = new EventEmitter<void>();
  /**Memorizza da dove viene lanciata la creazione del modale */
  @Input() public parent: cercaGlossariParent;
  /**Elenco dei tipi di glossario disponibili */
  elencoTipiGlossario: GlossaryType[];
  /**Form per filtrare l'elenco dei glossari */
  formCercaGlossari: FormGroup;
  /**Voce di glossario in selezione principale */
  primarySelection: Glossary;
  /**Array di voci glossario in selezione secondaria */
  secondarySelections: Glossary[];
  /**Datasource della mat table */
  dataSource: Glossary[] = [];
  /**Array delle colonne visibili */
  colonneVisibili: string[] = ['type', 'name', 'transliteration', 'translation', 'original', 'username', 'locked'];
  /**Numero totale di risultati disponibili */
  resultsLength = 0;
  /**Indice iniziale di visualizzazione in table */
  startIndex: number = 0;
  /**Indice finale di visualizzazione in table */
  endIndex: number = 25;
  /**Paginatore per la mat table */
  @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;
  /**Opzioni della scrollbar */
  options = { autoHide: true, scrollbarMinSize: 100 };
  /**Se il contenuto della tabella è in caricamento */
  isLoadingModal: boolean;
  /**Voce di glossario selezionata per l'associazione da modale */
  modalSelection: Glossary;

  /**
   * Costruttore per il componente del modale cerca glossario
   * @param store {Store<AppState>} stato a livello dell'applicazione
   * @param glossariService {GlossariService} servizi per la sezione glossari
   */
  constructor(
    private store: Store<AppState>,
    private glossariService: GlossariService,
    private utilityService: UtilityService
  ) {
    this.subs.sink = this.store.select(getMyGlossari).subscribe(d => {
      this.primarySelection = d.voceSelezionata;
      this.secondarySelections = d.secondarySelections;
      this.dataSource = d.elencoVociGlosModale;
      this.resultsLength = d.totalCountModal;
      this.isLoadingModal = d.isLoadingTabVociModale;
    });
  }

  /**Metodo dell'interfaccia OnInit nel quale sottoscrivo ai dati */
  ngOnInit() {
    this.store.select(getMyAuth).pipe(take(1)).subscribe(data => {
      this.elencoTipiGlossario = data.glossaryTypeList;
    });

    this.formCercaGlossari = new FormGroup({
      name: new FormControl(this.parent === cercaGlossariParent.modificaAssocia ? this.primarySelection.name : ''),
      transliteration: new FormControl(''),
      translation: new FormControl(''),
      original: new FormControl(''),
      type: new FormControl(''),
      locked: new FormControl(this.parent === cercaGlossariParent.modificaAssocia ? true : false)
    });

    this.store.dispatch(new glossariActions.SetRowInterval({ sezione: glossariActions.tabellaGlossari.modal, fromRow: 0, toRow: 25 }));
    let filters: SingleFilter[] = [];
    if (this.formCercaGlossari.get('name').value !== '') {
      filters.push(this.utilityService.createFilter('name', this.formCercaGlossari.get('name').value, false, false));
    }
    if (this.formCercaGlossari.get('locked').value !== false) {
      filters.push(this.utilityService.createFilter('locked', this.formCercaGlossari.get('locked').value, false, false));
    }
    if (filters.length > 0) {
      this.store.dispatch(new glossariActions.ApplyFilters({ sezione: glossariActions.tabellaGlossari.modal, elencoFiltri: filters.length === 1 ? filters[0] : { operator: operatorValue.and, filters: filters } }));
    }
    this.store.dispatch(new glossariActions.StartGetListGlos(glossariActions.tabellaGlossari.modal));

    this.subs.sink = this.glossariService.getGoToFirstPage().subscribe(d => {
      if (d === glossariActions.tabellaGlossari.modal) {
        this.paginator.pageIndex = 0;
        this.paginator.page.emit({ length: this.resultsLength, pageIndex: 0, pageSize: this.paginator.pageSize, previousPageIndex: this.paginator.pageIndex }); //rimando alla prima pagina della tabella
      }
    });

  }

  /**Metodo eseguito dopo l'inizializzazione della vista del componente, contiene la sottoscrizione al paginatore */
  ngAfterViewInit() {
    this.subs.sink = this.paginator.page.pipe(
      map(data => data)
    ).subscribe(d => {
      this.startIndex = d.pageIndex * d.pageSize;
      this.endIndex = (d.pageIndex + 1) * d.pageSize;
      this.store.dispatch(new glossariActions.SetRowInterval({ sezione: glossariActions.tabellaGlossari.modal, fromRow: this.startIndex, toRow: this.endIndex }));
      this.store.dispatch(new glossariActions.StartGetListGlos(glossariActions.tabellaGlossari.modal));
    });

    if (this.parent === cercaGlossariParent.modificaAssocia) {
      this.onSubmitFiltersModal();
    }

  }

  /**Metodo che applica i filtri del form e recupera l'elenco di voci glossario corrispondente ai parametri */
  onSubmitFiltersModal() {
    this.modalSelection = null;
    let elencoFiltri: SingleFilter[] = [];
    for (let key in this.formCercaGlossari.value) {
      if (this.formCercaGlossari.get(key).value !== '' && this.formCercaGlossari.get(key).value !== 'any' && this.formCercaGlossari.get(key).value !== false) {
        elencoFiltri.push(this.utilityService.createFilter(key, this.formCercaGlossari.get(key).value, false, false));
      }
    }
    this.store.dispatch(new glossariActions.ApplyFilters({ sezione: glossariActions.tabellaGlossari.modal, elencoFiltri: elencoFiltri.length > 0 ? (elencoFiltri.length === 1 ? elencoFiltri[0] : { operator: operatorValue.and, filters: elencoFiltri }) : null }));
    this.glossariService.sendGoToFirstPage(glossariActions.tabellaGlossari.modal);
  }

  /**Metodo che gestisce il click sul pulsante associa, direzionandolo all'azione corretta dello store in base al parent del modale (e quindi alla sua funzione) */
  onAssociaSelezioneHandler() {
    if (this.parent === cercaGlossariParent.aggiuntaVediAnche) {
      this.store.dispatch(new glossariActions.StartAddSeeAlso({ idGlos: this.primarySelection.id, seeAlso: this.modalSelection }));
    } else if (this.parent === cercaGlossariParent.modificaAssocia) {
      let indexArray = [this.primarySelection.id];
      if (this.secondarySelections && this.secondarySelections.length > 0) {
        for (let glos of this.secondarySelections) {
          indexArray.push(glos.id);
        }
      }
      indexArray = indexArray.filter(glosId => glosId !== this.modalSelection.id);
      if (indexArray.length > 0) {
        this.store.dispatch(new glossariActions.StartAssociateMultipleGlos({ target: this.modalSelection.id, source: indexArray }));
        this.onClose();
      }
    }
  }

  /**Metodo che emette l'evento di chiusura al click sul pulsante relativo*/
  onClose() {
    this.store.dispatch(new glossariActions.DestroyModalCercaGlossario());
    this.close.emit();
  }

  /**
   * Metodo che setta la voce di glossario selezionata nel modale
   * @param voce {Glossary} la voce di glossario selezionata nella tabella del modale
   */
  onSelectModalGlossary(voce: Glossary) {
    this.modalSelection = voce;
  }

  /**Metodo dell'interfaccia OnDestroy dove vengono rimosse le sottoscrizioni eventualmente presenti */
  ngOnDestroy() {
    this.subs.unsubscribe();
  }
}
